import React, { useEffect, useRef, useState } from "react";
import LandscapeProgramLayout from "../LandscapeProgramLayout";
import BottomNav from "../BottomNav";
import cn from "../../../utils/cn";
import { Pause, Play, Triangle } from "../../Icons";
import { CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { useAuthContext } from "../../../context/auth";
import EventEmitter from "eventemitter3";
import useCountDown from "../../../hooks/useCountDown";
import animation from "../../animation.json";
import keep from "../../arrowRed.json";

import interpolate from "../../../utils/interpolate";
import Lottie from "react-lottie";
import useAudio from "../../../hooks/useAudio";
import useClassHeartRate from "../../../hooks/useClassHeartRate";
import useMemberWeights from "../../../hooks/useMemberWeights";
import {
  CoinBoardScreen,
  CoinMembers,
  CoinTimerPanel,
  SET_LABEL_GAP,
  SET_LABEL_WIDTH,
  useCoinBoardSize,
} from "./WorkoutCoinBoard";

function Line() {
  return (
    <svg
      width="4"
      height="92"
      viewBox="0 0 4 92"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <line
        x1="2"
        y1="-8.74228e-08"
        x2="2"
        y2="92"
        stroke="url(#paint0_linear_5264_1049)"
        stroke-width="4"
        stroke-dasharray="6 6"
      />
      <defs>
        <linearGradient
          id="paint0_linear_5264_1049"
          x1="0"
          y1="0"
          x2="-1.99976"
          y2="0.0217366"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#E3FAFF" />
          <stop offset="1" stop-color="#53CAF5" />
        </linearGradient>
      </defs>
    </svg>
  );
}

// 코인(실시간 심박) 화면 세트 바에 쓰는 구간 점선.
function CoinLine({ height }: { height: number }) {
  return (
    <svg width="4" height={height} viewBox={`0 0 4 ${height}`} fill="none">
      <line
        x1="2"
        y1="0"
        x2="2"
        y2={height}
        stroke="url(#booster_coin_line)"
        strokeWidth="4"
        strokeDasharray="6 6"
      />
      <defs>
        <linearGradient
          id="booster_coin_line"
          x1="0"
          y1="0"
          x2="-2"
          y2="0.05"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#E3FAFF" />
          <stop offset="1" stopColor="#53CAF5" />
        </linearGradient>
      </defs>
    </svg>
  );
}

// 세트 바 위 Easy·Normal·Hard 구간 점선 위치(바 너비 대비). 글자는 점선 위 가운데에 둔다.
const SECTION_LINES = ["50%", "66.6%", "83.2%"];
const SECTION_LABELS = [
  { left: SECTION_LINES[0], text: "Easy", gradient: "from-[#E3FAFF] to-[#53CAF5]" },
  { left: SECTION_LINES[1], text: "Normal", gradient: "from-[#FFF5E0] to-[#F6B731]" },
  { left: SECTION_LINES[2], text: "Hard", gradient: "from-[#FFEBEB] to-[#FF5757]" },
];

function Fit({
  active,
  callback,
  number,
  round,
  remainingTime,
  compact,
}: {
  number: number;
  round: number;
  active: boolean;
  callback: () => void;
  remainingTime: number;
  // 코인 운영 지점(실시간 심박 화면)용 세트 바.
  compact?: boolean;
}) {
  const time = 60;
  const count = Math.min(remainingTime, 60);
  const { did } = useAuthContext();
  const { boosterBarHeight: barHeight, setLabelClass } = useCoinBoardSize();
  const isCompleted = useRef(false);
  const hasPlayed30 = useRef(false);
  const hasPlayed40 = useRef(false);
  const hasPlayed50 = useRef(false);
  const hasPlayed3 = useRef(false);

  const play = useAudio("countdown.mp3");
  const play30 = useAudio("second30_delayed.mp3");
  const play40 = useAudio("second40_delayed.mp3");
  const play50 = useAudio("second50_delayed.mp3");

  const getProgressBarGradient = () => {
    if (count <= 30 && count > 20) {
      return "linear-gradient(270deg, #F6B731 0%, rgba(246, 183, 49, 0) 100%)";
    } else if (count <= 20 && count > (round === 6 && number === 3 ? -1 : 10)) {
      return "linear-gradient(270deg, #FF5757 0%, rgba(255, 87, 87, 0) 100%)";
    } else {
      return "linear-gradient(270deg, #53CAF5 0%, rgba(44, 218, 0, 0.00) 100%)";
    }
  };

  const getCircleGradient = () => {
    if (count <= 30 && count > 20) {
      return "linear-gradient(135deg, #FFF5E0 0%, #F6B731 100%)";
    } else if (count <= 20 && count > (round === 6 && number === 3 ? -1 : 10)) {
      return "linear-gradient(135deg, #FFEBEB 0%, #FF5757 100%)";
    } else {
      return "linear-gradient(135deg, #E3FAFF 0%, #53CAF5 100%)";
    }
  };

  const getHeartbeatAnimation = () => {
    if (count <= 30 && count > 20) {
      return "heartbeat-normal 1s ease-in-out infinite";
    } else if (count <= 20 && count > 10) {
      return "heartbeat-fast 0.6s ease-in-out infinite";
    } else {
      return "heartbeat-slow 1.5s ease-in-out infinite";
    }
  };

  useEffect(() => {
    if (active && !isCompleted.current) {
      if (count === 0) {
        callback();
        isCompleted.current = true;
      }
      if (count === 32 && !hasPlayed30.current) {
        did === "1" && play30();
        hasPlayed30.current = true;
      }
      if (count === 20 && !hasPlayed40.current) {
        did === "1" && play40();
        hasPlayed40.current = true;
      }
      if (count === 10 && !hasPlayed50.current) {
        did === "1" && play50();
        hasPlayed50.current = true;
      }
      if (count === 3 && !hasPlayed3.current) {
        did === "1" && play();
        hasPlayed3.current = true;
      }
    }

    // Reset flags when count increases (new round)
    if (count > 30) {
      hasPlayed30.current = false;
      hasPlayed40.current = false;
      hasPlayed50.current = false;
      hasPlayed3.current = false;
    }
  }, [active, count]);

  const fillWidth = `${interpolate(((60 - count) / time) * 100)}%`;

  if (compact) {
    // 테두리(4)·안쪽 여백(4)을 빼고 위아래로 4씩 띄운 채움 높이.
    const fill = barHeight - 16;
    return (
      <div className="flex items-center" style={{ gap: SET_LABEL_GAP }}>
        <h1
          className={cn("shrink-0 text-white", setLabelClass)}
          style={{ width: SET_LABEL_WIDTH }}
        >
          {number}SET
        </h1>
        <div
          className="flex-1 rounded-[100px] p-[4px] relative"
          style={{
            height: barHeight,
            backgroundImage: "linear-gradient(135deg, #E3FAFF 0%, #53CAF5 100%)",
          }}
        >
          {SECTION_LINES.map((left) => (
            <div
              key={left}
              className="absolute top-[1px] -translate-x-1/2 z-10 flex"
              style={{ left }}
            >
              <CoinLine height={barHeight - 3} />
            </div>
          ))}
          <div className="w-full h-full bg-bg rounded-[100px] px-[4px] flex items-center">
            <div
              className="rounded-[100px] flex justify-end"
              style={{
                height: fill,
                backgroundImage: getProgressBarGradient(),
                transition: "all 0.5s ease",
                width: fillWidth,
                minWidth: fill,
              }}
            >
              <div
                className="rounded-[100px]"
                style={{
                  width: fill,
                  height: fill,
                  backgroundImage: getCircleGradient(),
                  animation: getHeartbeatAnimation(),
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gap-[50px] flex items-center">
      <h1 className="text-[48px] font-bold text-white">{number}SET</h1>
      <div
        className="w-[1000px] h-[100px] rounded-[100px] flex items-center justify-center relative"
        style={{
          border: "4px solid transparent",
          backgroundImage: "linear-gradient(135deg, #E3FAFF 0%, #53CAF5 100%)",
          backgroundOrigin: "border-box",
          // backgroundClip: "content-box, border-box",
        }}
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2">
          <Line />
        </div>
        <div className="absolute top-0 left-[66.6%] -translate-x-1/2">
          <Line />
        </div>
        <div className="absolute top-0 left-[83.2%] -translate-x-1/2">
          <Line />
        </div>
        <div className="w-full h-full bg-bg rounded-[100px] flex items-center">
          <div
            className="w-[984px] h-[84px] rounded-[84px] flex justify-end mx-2 relative"
            style={{
              backgroundImage: getProgressBarGradient(),
              transition: "all 0.5s ease",
              width: fillWidth,
              minWidth: "84px",
            }}
          >
            <div
              className="w-[84px] h-[84px] rounded-[100px]"
              style={{
                backgroundImage: getCircleGradient(),
                animation: getHeartbeatAnimation(),
              }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** 실시간 심박 화면의 부스터 세트 영역: Easy·Normal·Hard 줄, 세트 바 3개, 다음 스테이션 안내. */
function BoosterCoinSets({
  children,
  guide,
}: {
  children: React.ReactNode;
  guide: React.ReactNode;
}) {
  const { isMember } = useCoinBoardSize();
  return (
    <div className="flex flex-col justify-between h-full">
      <div className={cn("flex flex-col", isMember ? "gap-[24px]" : "gap-[20px]")}>
        <div className="flex" style={{ gap: SET_LABEL_GAP }}>
          <div className="shrink-0" style={{ width: SET_LABEL_WIDTH }} />
          <div
            className={cn(
              "relative flex-1 leading-none font-['TheJamsil6ExtraBold']",
              // 구간 점선 간격(바 너비의 약 1/6)에 'Normal'이 들어가는 크기.
              isMember ? "h-[30px] text-[30px]" : "h-[28px] text-[28px]",
            )}
          >
            {SECTION_LABELS.map((label) => (
              <p
                key={label.text}
                className={cn(
                  "absolute top-0 -translate-x-1/2 bg-gradient-to-br bg-clip-text text-transparent",
                  label.gradient,
                )}
                style={{ left: label.left }}
              >
                {label.text}
              </p>
            ))}
          </div>
        </div>
        <div className={cn("flex flex-col", isMember ? "gap-[36px]" : "gap-[28px]")}>
          {children}
        </div>
      </div>
      {/* 안내가 없을 때도 자리를 둬서 세트 바 위치가 흔들리지 않게 한다. */}
      <div className="flex gap-[40px] items-center justify-center h-[60px]">
        {guide}
      </div>
    </div>
  );
}

function WorkoutType2({ round, last }: { round: number; last?: boolean }) {
  const {
    emitter,
    sendSetStep,
    step,
    sendStartCountDown,
    sendStopCountDown,
    did,
    classId,
    isCoinEnabled,
    isHeartRateEnabled,
    timer,
  } = useAuthContext();
  // 코인 운영 + 운영설정 '심박수' 가 켜진 지점의 수업이면 실시간 심박·예상 포인트 화면을
  // 보여준다(웨이트 WORK OUT 과 같은 조건). 꺼져 있으면 기존 부스터 화면 그대로다.
  const showHeartRate =
    isCoinEnabled && isHeartRateEnabled && classId !== undefined;
  const heartRateBoard = useClassHeartRate(classId, showHeartRate);
  const memberWeights = useMemberWeights(
    classId,
    showHeartRate,
    heartRateBoard?.members.length ?? 0,
  );
  const [showNext, setShowNext] = useState(false);
  const playNext = useAudio("nextstation.mp3");
  const playKeep = useAudio("keep.mp3");

  const [activeNumber, setActiveNumber] = useState(1);
  const { count, reset, start, stop, stopped } = useCountDown(
    180,
    () => {
      if (last) {
        setTimeout(() => {
          sendSetStep(step + 1);
        }, 3000);
      } else {
        sendSetStep(step + 1);
      }
    },
    true
  );

  useEffect(() => {
    const currentSetIndex = Math.floor((180 - count) / 60);
    if (currentSetIndex !== activeNumber - 1 && currentSetIndex < 3) {
      setActiveNumber(currentSetIndex + 1);
    }
  }, [count]);

  useEffect(() => {
    if (count === 9) {
      setShowNext(true);
      setTimeout(() => {
        if (round === 6) {
          did === "1" && playKeep();
        } else {
          did === "1" && playNext();
        }
      }, 1000);
    }
  }, [count, round, activeNumber, last, did]);
  useEffect(() => {
    start();

    emitter.current.on("resume", () => {
      start();
    });
    emitter.current.on("pause", () => {
      stop();
    });
    return () => {
      emitter.current.off("resume");
      emitter.current.off("pause");
    };
  }, []);

  const heartbeatKeyframes = (
    <style>{`
      @keyframes heartbeat-slow {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }
      @keyframes heartbeat-normal {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }
      @keyframes heartbeat-fast {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.15); }
      }
    `}</style>
  );

  const fits = (compact: boolean) =>
    Array(3)
      .fill(0)
      .map((_, i) => (
        <Fit
          round={round}
          active={i <= activeNumber - 1 && !stopped}
          callback={() => {
            if (i === 2) {
              return;
            } else {
              setActiveNumber((prev) => prev + 1);
            }
          }}
          number={i + 1}
          key={i}
          remainingTime={Math.max(0, count - 60 * (2 - i))}
          compact={compact}
        />
      ));

  const setRemaining = Math.max(0, count - 60 * (3 - activeNumber));
  const remainingTime = `${String(Math.floor(setRemaining / 60)).padStart(
    2,
    "0",
  )}:${String(setRemaining % 60).padStart(2, "0")}`;

  const toggleCountDown = () => {
    if (stopped) {
      sendStartCountDown();
    } else {
      sendStopCountDown();
    }
  };

  // 마지막 9초에 다음 스테이션(6라운드는 KEEP GOING) 안내.
  const nextGuide = (width: number, height: number) =>
    round < 6 ? (
      <>
        <h1 className="text-[48px] font-extrabold gradient-text uppercase">
          next station
        </h1>
        {/* react-lottie 가 margin: 0 auto 를 넣어서, 감싸지 않으면 flex 안에서 남는 너비를 다 차지한다. */}
        <div className="shrink-0">
          <Lottie
            options={{
              loop: true,
              animationData: animation,
              rendererSettings: {
                preserveAspectRatio: "xMidYMid slice",
              },
            }}
            width={width}
            height={height}
            isClickToPauseDisabled
          />
        </div>
      </>
    ) : (
      <>
        <h1 className="text-[48px] font-extrabold gradient-text-red uppercase">
          keep going
        </h1>
        {/* react-lottie 가 margin: 0 auto 를 넣어서, 감싸지 않으면 flex 안에서 남는 너비를 다 차지한다. */}
        <div className="shrink-0">
          <Lottie
            options={{
              loop: true,
              animationData: keep,
              rendererSettings: {
                preserveAspectRatio: "xMidYMid slice",
              },
            }}
            width={width}
            height={height}
            isClickToPauseDisabled
          />
        </div>
      </>
    );

  if (showHeartRate) {
    return (
      <>
        {heartbeatKeyframes}
        <CoinBoardScreen
          sets={
            <BoosterCoinSets guide={showNext ? nextGuide(129, 60) : null}>
              {fits(true)}
            </BoosterCoinSets>
          }
          timer={
            <CoinTimerPanel
              round={`${round}/6R`}
              remaining={remainingTime}
              stopped={stopped}
              onToggle={toggleCountDown}
            />
          }
          members={
            <CoinMembers
              members={heartRateBoard?.members ?? []}
              weights={memberWeights}
              // 칼로리는 하단에 보이는 수업 경과 시간 기준, 10초 단위로만 올린다.
              elapsedMinutes={
                (Math.floor(timer.getElapsedRunningTime() / 1000 / 10) * 10) /
                60
              }
            />
          }
        />
      </>
    );
  }


  return (
    <LandscapeProgramLayout step={4}>
      <>
        {heartbeatKeyframes}
        <div className="bg-bg w-full relative h-full flex">
          <div
            className={cn(
              "absolute left-[680px] flex gap-[35px]",
              did === "1" ? "top-[215px]" : "top-[150px]",
            )}
          >
            <div className="text-[44px] font-extrabold leading-[70px] text-center bg-gradient-to-br from-[#E3FAFF] to-[#53CAF5] bg-clip-text text-transparent">
              Easy
            </div>
            <div className="text-[44px] font-extrabold leading-[70px] text-center bg-gradient-to-br from-[#FFF5E0] to-[#F6B731] bg-clip-text text-transparent">
              Normal
            </div>
            <div className="text-[44px] font-extrabold leading-[70px] text-center bg-gradient-to-br from-[#FFEBEB] to-[#FF5757] bg-clip-text text-transparent">
              Hard
            </div>
          </div>
          <div
            className={cn(
              "flex flex-col ml-[59px] flex-1 justify-center",
              did === "1" ? "gap-[80px]" : "gap-[50px]",
            )}
          >
            {fits(false)}
          </div>
          <div className="absolute left-[1440px] top-1/2 -translate-y-1/2 flex flex-col items-center w-[420px]">
            <h2 className="text-[80px] font-bold text-white">{round}/6R</h2>
            <h2 className="text-[140px] font-bold text-white">
              {remainingTime}{" "}
            </h2>
            <button onClick={toggleCountDown}>
              {stopped ? <Play /> : <Pause />}
            </button>
          </div>
          {showNext && (
            <div className="absolute bottom-[100px] left-1/2 -translate-x-1/2 flex gap-[20px] items-center">
              {nextGuide(142, 66)}
            </div>
          )}
        </div>
      </>
      <BottomNav />
    </LandscapeProgramLayout>
  );
}

export default WorkoutType2;
